// Überwachung: Alarmverlauf aller Bildschirme und Alarm-Einstellungen (E-Mail-Empfänger, Ereignisse, Ruhezeit,
// Verzögerung, Tageszusammenfassung, automatische Screenshots).
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { mk, useAlerts, useMonitoringSettings, type MonitoringSettings } from '../api/monitoring';
import { can, useDevices, useMe } from '../api/queries';
import { Icon } from '../components/Icon';
import { AlertList } from '../components/Monitoring';
import { useToast } from '../components/toast';
import { Field, PageHead, Spinner } from '../components/ui';

type Tab = 'alerts' | 'settings';

export function MonitoringPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const canSettings = can(me, 'tenant.settings.manage');
  const tab: Tab = params.get('tab') === 'settings' && canSettings ? 'settings' : 'alerts';
  const setTab = (k: Tab) => setParams(k === 'alerts' ? {} : { tab: k }, { replace: true });
  return (
    <>
      <PageHead title={t('monitoring.title')} sub={t('monitoring.sub')} />
      {canSettings && (
        <div className="tabs" role="tablist">
          {(['alerts', 'settings'] as Tab[]).map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{t(`monitoring.tab.${k}`)}</button>
          ))}
        </div>
      )}
      {tab === 'alerts' ? <AlertsTab /> : <SettingsTab />}
    </>
  );
}

function AlertsTab() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const { data: devices = [] } = useDevices();
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const alerts = useAlerts({ status, kind, deviceId });
  const open = alerts.items.filter((a) => a.status === 'open').length;
  return (
    <>
      <div className="toolbar">
        <select aria-label={t('monitoring.col.status')} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('monitoring.filter.allStatus')}</option>
          <option value="open">{t('monitoring.status.open')}</option>
          <option value="resolved">{t('monitoring.status.resolved')}</option>
        </select>
        <select aria-label={t('monitoring.col.kind')} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t('monitoring.filter.allKinds')}</option>
          <option value="offline">{t('monitoring.kind.offline')}</option>
          <option value="error">{t('monitoring.kind.error')}</option>
        </select>
        <select aria-label={t('monitoring.col.screen')} value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
          <option value="">{t('monitoring.filter.allScreens')}</option>
          {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {!status && !kind && !deviceId && open > 0 && <span className="pill warn">{t('monitoring.openCount', { count: open })}</span>}
      </div>
      <div className="card" style={{ padding: 10 }}>
        {alerts.isLoading ? <Spinner /> : (
          <AlertList alerts={alerts.items} showDevice canManage={can(me, 'devices.manage')} onChanged={() => qc.invalidateQueries({ queryKey: ['monitoring'] })} />
        )}
        {alerts.hasMore && <div className="row" style={{ justifyContent: 'center', marginTop: 10 }}><button className="btn" onClick={() => void alerts.more()}>{t('monitoring.more')}</button></div>}
      </div>
    </>
  );
}

type Body = Omit<MonitoringSettings, 'rowVersion' | 'smtpConfigured' | 'timezone'>;
type Form = Omit<Body, 'recipients'> & { recipients: string };

const bodyOf = (s: MonitoringSettings): Body => {
  const o: Partial<MonitoringSettings> = { ...s };
  delete o.rowVersion;
  delete o.smtpConfigured;
  delete o.timezone;
  return o as Body;
};
const formToBody = (f: Form): Body => ({ ...f, recipients: f.recipients.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean) });

const INTERVALS = [0, 15, 30, 60, 240];
const RETENTION = [6, 24, 72, 168];

function SettingsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading } = useMonitoringSettings();
  const [f, setF] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (s) setF({ ...bodyOf(s), recipients: s.recipients.join('\n') });
  }, [s]);
  if (isLoading || !s || !f) return <Spinner />;
  const up = <K extends keyof Form>(k: K, v: Form[K]) => setF({ ...f, [k]: v });
  const body = formToBody(f);

  const save = async () => {
    setBusy(true);
    try {
      await api.put<MonitoringSettings>('/monitoring/settings', body, { ifMatch: s.rowVersion });
      await qc.invalidateQueries({ queryKey: mk.settings });
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const testMail = async () => {
    try {
      await api.post('/monitoring/settings/test-mail', {});
      toast.show(t('monitoring.settings.testSent', { to: s.recipients.join(', ') }));
    } catch (e) {
      toast.error(e);
    }
  };
  const dirty = JSON.stringify(body) !== JSON.stringify(bodyOf(s));

  return (
    <div className="col" style={{ maxWidth: 760, gap: 16 }}>
      {!s.smtpConfigured && <div className="hint warn" role="status">{t('monitoring.settings.noSmtp')}</div>}
      <div className="card col">
        <h2 style={{ margin: 0 }}>{t('monitoring.settings.email')}</h2>
        <label className="check"><input type="checkbox" checked={f.alertsEnabled} onChange={(e) => up('alertsEnabled', e.target.checked)} />{t('monitoring.settings.enabled')}</label>
        <Field label={t('monitoring.settings.recipients')} hint={t('monitoring.settings.recipientsHint')}>
          <textarea rows={3} value={f.recipients} onChange={(e) => up('recipients', e.target.value)} placeholder="technik@firma.ch" />
        </Field>
        <div className="field">{t('monitoring.settings.events')}
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <label className="check"><input type="checkbox" checked={f.notifyOffline} onChange={(e) => up('notifyOffline', e.target.checked)} />{t('monitoring.settings.offline')}</label>
            <label className="check"><input type="checkbox" checked={f.notifyErrors} onChange={(e) => up('notifyErrors', e.target.checked)} />{t('monitoring.settings.errors')}</label>
            <label className="check"><input type="checkbox" checked={f.notifyRecovery} onChange={(e) => up('notifyRecovery', e.target.checked)} />{t('monitoring.settings.recovery')}</label>
          </div>
        </div>
        <div className="grid-2">
          <Field label={t('monitoring.settings.delay')} hint={t('monitoring.settings.delayHint')}>
            <input type="number" min={3} max={1440} value={f.offlineDelayMinutes} onChange={(e) => up('offlineDelayMinutes', Number(e.target.value))} />
          </Field>
          <Field label={t('monitoring.settings.errorResolve')} hint={t('monitoring.settings.errorResolveHint')}>
            <input type="number" min={5} max={1440} value={f.errorResolveMinutes} onChange={(e) => up('errorResolveMinutes', Number(e.target.value))} />
          </Field>
        </div>
        <div className="field">{t('monitoring.settings.quiet')}
          <label className="check"><input type="checkbox" checked={!!f.quietHours} onChange={(e) => up('quietHours', e.target.checked ? { from: '22:00', until: '07:00' } : null)} />{t('monitoring.settings.quietOn')}</label>
          {f.quietHours && (
            <div className="row">
              <label className="row small">{t('monitoring.settings.quietFrom')}<input type="time" value={f.quietHours.from} onChange={(e) => up('quietHours', { ...f.quietHours!, from: e.target.value })} /></label>
              <label className="row small">{t('monitoring.settings.quietUntil')}<input type="time" value={f.quietHours.until} onChange={(e) => up('quietHours', { ...f.quietHours!, until: e.target.value })} /></label>
            </div>
          )}
        </div>
        <div className="field">{t('monitoring.settings.digest')}
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <label className="check"><input type="checkbox" checked={f.digestEnabled} onChange={(e) => up('digestEnabled', e.target.checked)} />{t('monitoring.settings.digestOn')}</label>
            <select aria-label={t('monitoring.settings.digestHour')} disabled={!f.digestEnabled} value={f.digestHour} onChange={(e) => up('digestHour', Number(e.target.value))}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>)}
            </select>
          </div>
        </div>
        <Field label={t('monitoring.settings.language')}>
          <select value={f.locale} onChange={(e) => up('locale', e.target.value as 'de' | 'en')}><option value="de">Deutsch</option><option value="en">English</option></select>
        </Field>
        <div className="small muted">{t('monitoring.settings.timezone', { tz: s.timezone })}</div>
      </div>

      <div className="card col">
        <h2 style={{ margin: 0 }}>{t('monitoring.settings.screenshots')}</h2>
        <div className="grid-2">
          <Field label={t('monitoring.settings.interval')}>
            <select value={f.screenshotIntervalMinutes} onChange={(e) => up('screenshotIntervalMinutes', Number(e.target.value))}>
              {INTERVALS.map((m) => <option key={m} value={m}>{m ? t('monitoring.settings.every', { count: m }) : t('monitoring.settings.intervalOff')}</option>)}
            </select>
          </Field>
          <Field label={t('monitoring.settings.retention')}>
            <select value={f.screenshotRetentionHours} onChange={(e) => up('screenshotRetentionHours', Number(e.target.value))}>
              {[...new Set([...RETENTION, f.screenshotRetentionHours])].sort((a, b) => a - b).map((h) => <option key={h} value={h}>{t('monitoring.settings.hours', { count: h })}</option>)}
            </select>
          </Field>
        </div>
        <div className="small muted">{t('monitoring.settings.privacy')}</div>
      </div>

      <div className="row">
        <button className="btn primary" onClick={() => void save()} disabled={busy || !dirty}><Icon name="check" />{t('common.save')}</button>
        <button className="btn" onClick={() => void testMail()} disabled={!s.smtpConfigured || !s.recipients.length || dirty} title={dirty ? t('monitoring.settings.saveFirst') : ''}>
          <Icon name="send" />{t('monitoring.settings.test')}
        </button>
      </div>
    </div>
  );
}
